import { renderCell } from 'components/cell/cell'
import { isStore } from './location'
import { HandleDataClick } from 'components/cell/cell'
import Icon, {
    CloseIcon,
    CheckmarkIcon,
    WarningIcon,
} from '@design-systems/react-icons'
import React from 'react'
import { formatNegative } from './number-format'

export interface Link {
    href?: string
    drill?: string
}

export interface Cell {
    raw?: string
    value: string
    link?: Link
    url?: string
    childOf?: string
    locationId?: string | number
    is_link?: boolean
    color?: string
    className?: string
    id?: string
    icon?: React.ReactNode
}

export interface Row {
    rowCells: Cell[]
    children?: {
        rows: {
            rowCells: Cell[]
        }[]
    }
}

export interface TableData {
    headings?: Cell[]
    rows: Cell[][]
    footer?: Cell[]
}

export interface TitledTableData {
    title: string
    table: TableData
}

export type LoyaltyCellData = {
    value: string
    raw: string
}

export type LoyaltyRowData = LoyaltyCellData[]

export type LoyaltyRowsData = LoyaltyRowData[]

export type CellData = {
    value: string
    raw: string
    color?: string
}

export type RowData = CellData[]

export type RowsData = RowData[]

export const getNegativeStyle = (value: string): string => {
    let fontColor: any
    // Don't apply negative styling to "--" values
    if (value?.startsWith('-') && value !== '--') {
        fontColor = 'hc-clr-error'
        return fontColor
    }
    return ''
}
export const getMultipleSlices = (
    array: Cell[],
    ...args: (number | [number, number])[]
): Cell[] =>
    args.flatMap((arg) => {
        if (typeof arg === 'number') {
            return array.slice(arg)
        }
        return array.slice(arg[0], arg[1])
    })
export const addLinkToFirstColumn = (arr: Cell[][]) =>
    [...arr].map((row) => {
        row[0].link = {}

        return row
    })
export const removeLinkFromFirstColumn = (arr: Cell[][]) =>
    [...arr].map((row) => {
        row[0].link = undefined

        return row
    })

export const addHyperlinkToFirstColumn = (
    data: TableData,
    currentLocation: string,
    pathname?: string,
    skipRowCount?: number,
) => {
    if (data) {
        const { rows } = data
        if (rows) {
            data.rows = [...rows]?.map((row, index) => {
                if (skipRowCount && index < skipRowCount) {
                    return row
                }

                const locationCell = row[0]
                if (
                    !isStore(currentLocation) &&
                    locationCell.value !== currentLocation
                ) {
                    const currentUrl = new URL(window.location.href)
                    const url = new URL(
                        pathname ?? currentUrl.pathname,
                        window.location.origin,
                    )
                    url.search = currentUrl.search
                    if (locationCell.value?.toLowerCase().includes('target')) {
                        url.searchParams.delete('locationId')
                    } else {
                        url.searchParams.set(
                            'locationId',
                            locationCell.value || '',
                        )
                        locationCell.url = url.pathname + url.search
                    }
                }
                return row
            })
        }
    }
    return data
}

export const insertRows = (arr: Cell[][], index: number, items: Cell[][]) =>
    arr.splice(index, 0, ...items)

export type DataTableObjectType = {
    currentData: TableData
    widths: number[]
    handleClick?: (value: string) => void
    handleDataClick?: HandleDataClick
    pinFirstColumn?: boolean
    bottomLeftCell?: string
    selectedCell?: string
    sortable?: boolean
    isLoading?: boolean
    onSort?: (sortDirection: 'asc' | 'desc', field: string) => void
    sortType?: 'character' | 'numeric' | 'date' | 'boolean'
    defaultSort?: 'dsc' | 'asc'
    columnSortSettingsByIndex?: {
        [columnIndex: number]: {
            sortable?: boolean
            sortType?: 'character' | 'numeric' | 'date' | 'boolean'
            defaultSort?: 'dsc' | 'desc' | 'asc'
            onSort?: (sortDirection: 'asc' | 'dsc', field: string) => void
        }
    }
}
type FooterObjectType = {
    [key: string]:
        | string
        | {
              cellDisplay: React.ReactNode
              cellValue: string
          }
}

export const buildDataTableObject = ({
    currentData,
    widths,
    handleClick,
    handleDataClick,
    pinFirstColumn,
    bottomLeftCell,
    selectedCell = '',
    sortable = false,
    isLoading,
    onSort,
    sortType,
    defaultSort,
    columnSortSettingsByIndex,
}: DataTableObjectType) => {
    const getCellDisplay = (field: Cell) => {
        if (!field) return
        let cellObj: { [key: string]: string | React.ReactNode } = {
            cellValue: field?.raw || field?.value,
            cellDisplay: renderCell({
                cell: field,
                handleClick,
                handleDataClick,
                selectedCell,
                isLoading,
            }),
            isLink: field?.is_link,
            id: field?.id,
        }

        return cellObj
    }

    const rowsObj = currentData?.rows?.map((row) => {
        const rowObj = {} as any
        currentData?.headings?.forEach(
            (heading: Cell, headingIndex: number) => {
                const key =
                    heading.id ||
                    heading.value?.replaceAll(' ', '') ||
                    `column_${headingIndex}`
                const field = row[headingIndex]
                rowObj[key] = getCellDisplay(field)
            },
        )

        return rowObj
    })
    const footerObj = {} as FooterObjectType
    if (bottomLeftCell) {
        currentData?.footer?.unshift({ value: bottomLeftCell })
    }
    currentData?.footer?.forEach((footer, index) => {
        const key =
            currentData?.headings?.[index]?.id ||
            currentData?.headings?.[index]?.value?.replaceAll(' ', '') ||
            `column_${index}`
        const value = formatNegative(footer.value) as string
        footerObj[key] = {
            cellDisplay: renderCell({
                cell: footer,
                handleClick,
                isFooter: true,
                selectedCell,
            }),
            cellValue: value,
        }
    })
    if (Object.keys(footerObj).length > 0) {
        rowsObj.push(footerObj)
    }
    const obj = {
        defaultColDef: {
            sortable,
        },
        columnDefs: currentData?.headings?.map((heading, index) => {
            const fieldName =
                heading.id ||
                heading.value?.replaceAll(' ', '') ||
                `column_${index}`
            const columnOverride = columnSortSettingsByIndex?.[index] || {}

            return {
                field: fieldName,
                headerName: heading.value || `Column ${index + 1}`,
                isRowHeader: index === 0,
                resizable: false,
                width: widths[index],
                align: index === 0 ? 'left' : 'right',
                pinned: pinFirstColumn && index === 0,
                onSort: columnOverride.onSort ?? onSort,
                sortable: columnOverride.sortable ?? sortable,
                sortType: columnOverride.sortType ?? sortType,
                defaultSort: columnOverride.defaultSort ?? defaultSort,
            }
        }),
        rowData: rowsObj,
    }

    return obj
}

export const pickColumns = (
    data: TableData,
    columnIndexes: number[],
): TableData => {
    const newData = { ...data }
    newData.headings = columnIndexes
        .map((index) => data.headings?.[index])
        .filter((heading): heading is Cell => heading !== undefined)
    newData.rows = newData.rows.map((row) =>
        columnIndexes.map((index) => row[index]),
    )
    newData.footer = columnIndexes
        .map((index) => data.footer?.[index])
        .filter((heading): heading is Cell => heading !== undefined)

    return newData
}

export const filterRows = (
    data: TableData,
    predicate: (row: Cell[], index: number) => boolean,
): TableData => {
    return {
        ...data,
        rows: (data.rows ?? []).filter(predicate),
    }
}

export const applyConditionalFormatting = (
    value: string | number,
    color: string | undefined,
): Cell => {
    const cell: Cell = {
        value: String(value).replace(/\s+/g, ''),
    }

    const className = getColorClass(color)
    if (className) {
        cell.className = className

        // icons still live here
        if (className === 'hc-clr-error') {
            cell.icon = React.createElement(Icon, {
                svg: CloseIcon,
                className,
                size: 'sm',
                'aria-hidden': true,
            })
        } else if (className === 'hc-clr-success') {
            cell.icon = React.createElement(Icon, {
                svg: CheckmarkIcon,
                className,
                size: 'sm',
                'aria-hidden': true,
            })
        } else if (className === 'hc-clr-alert') {
            cell.icon = React.createElement(Icon, {
                svg: WarningIcon,
                className,
                size: 'sm',
                'aria-hidden': true,
            })
        }
    }

    return cell
}

export const getColorClass = (color?: string): string | undefined => {
    const normalized = color?.trim().toLowerCase()

    if (
        normalized === 'hc-clr-error' ||
        normalized === 'hc-clr-success' ||
        normalized === 'hc-clr-alert'
    ) {
        return normalized
    }

    // Support hex color mappings for backward compatibility
    if (normalized === '#cc0000') return 'hc-clr-error'
    if (normalized === '#c00000') return 'hc-clr-error'
    if (normalized === '#00a200') return 'hc-clr-success'
    if (normalized === '#007e33') return 'hc-clr-success'
    if (normalized === '#ffa500') return 'hc-clr-alert'

    return undefined
}

export const formatCellValueForDollarDisplay = (cell: Cell): Cell => {
    if (cell?.value === undefined || cell?.value === null) return cell

    const s = String(cell.value).trim()
    if (!s) return cell

    // Detect if value is negative (starts with - or is in parentheses)
    const isNegative =
        s.startsWith('-') || (s.startsWith('(') && s.endsWith(')'))

    // Determine if this value already has the $ sign
    const hasDollar = s.includes('$')

    let formattedValue = s

    // Handle different negative formats
    if (isNegative) {
        if (s.startsWith('-')) {
            // -123 or -$123 -> remove - and any $, then format
            const withoutNegative = s.slice(1).replace('$', '').trim()
            formattedValue = `($${withoutNegative})`
        } else if (s.startsWith('(') && s.endsWith(')')) {
            // (123) or ($123) -> ensure $ is present after opening paren
            const inner = s.slice(1, -1).replace('$', '').trim()
            formattedValue = `($${inner})`
        }
    } else {
        // Positive value - just add $ if not present
        if (!hasDollar) {
            formattedValue = `$${s}`
        }
    }

    return {
        ...cell,
        value: formattedValue,
        ...(isNegative && { className: 'hc-clr-error' }),
    }
}
